
import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import axiosInstance from "../context/axiosInstance";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { X, Loader2, Send, UserPlus } from "lucide-react";

const InviteGuestsModal = ({ event, onClose, onInvitesSent }) => {
  const [invitees, setInvitees] = useState([]);
  const [inviteInput, setInviteInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addInvitee = () => {
    if (!/^\+\d{8,15}$/.test(inviteInput))
      return toast.error("Invalid phone number format.");
    if (invitees.includes(inviteInput))
      return toast.error("Number already added.");
    setInvitees([...invitees, inviteInput]);
    setInviteInput("");
  };

  const removeInvitee = (numToRemove) => {
    setInvitees(invitees.filter((num) => num !== numToRemove));
  };

  // ✅ New submit function for this screen
  const handleSubmitNewInvitees = async () => {
    if (invitees.length === 0)
      return toast.error("Please add at least one invitee.");

    setIsSubmitting(true);
    toast
      .promise(
        axiosInstance.post(`/events/${event.event_id}/invitees`, {
          invitees: invitees,
        }),
        {
          loading: "Sending new invites...",
          success: (res) => {
            onInvitesSent(); // This function (passed as a prop) will refresh the main event details page
            onClose(); // Close this modal
            return res.data.message || "Invites sent successfully!";
          },
          error: (err) =>
            err.response?.data?.error || "Failed to send invites.",
        }
      )
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-md p-4 transition-all duration-300">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="relative w-full max-w-lg bg-surface/90 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl shadow-black/50 p-8"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-full p-2"
        >
          <X size={20} />
        </button>

        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mb-4 ring-4 ring-primary-500/10">
            <UserPlus size={32} className="text-primary-400" />
          </div>
          <h2 className="text-2xl font-black tracking-tighter text-white mb-1">
            Invite More Guests
          </h2>
          <p className="text-gray-400 text-sm">
            Add new people to <span className="text-white font-medium">"{event.eventName}"</span>.
          </p>
        </div>

        {/* Invitees Input Section */}
        <div className="bg-white/5 border border-white/10 p-4 rounded-xl mb-2">
          <div className="flex flex-wrap gap-2 mb-3">
            {invitees.map((num) => (
              <span
                key={num}
                className="flex items-center gap-2 bg-primary-500/20 text-primary-300 text-xs font-medium px-2.5 py-1 rounded-lg border border-primary-500/20"
              >
                {num}
                <button type="button" onClick={() => removeInvitee(num)} className="hover:text-white">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="relative">
            <PhoneInput
              defaultCountry="in"
              value={inviteInput}
              onChange={(phone) => setInviteInput(phone)}
              inputProps={{
                onKeyDown: (e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addInvitee();
                  }
                },
              }}
              inputClassName="!w-full !bg-transparent !border-none !text-white !text-base focus:!ring-0 !p-0 !h-auto placeholder-gray-500"
              className="flex items-center w-full"
              countrySelectorStyleProps={{
                buttonClassName: "!bg-transparent !border-none !pr-2",
                dropdownClassName: "!bg-surface !border-white/10 !text-black"
              }}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Enter a number and press <span className="text-gray-300 font-medium">Enter</span> to add.
        </p>

        <button
          onClick={handleSubmitNewInvitees}
          disabled={isSubmitting || invitees.length === 0}
          className="w-full mt-8 bg-white text-black font-bold py-4 rounded-xl text-lg hover:bg-gray-100 transition-all flex items-center justify-center gap-2 disabled:opacity-50 hover:shadow-lg hover:scale-[1.02]"
        >
          {isSubmitting ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Send size={18} />
          )}
          {isSubmitting ? "Sending..." : `Send ${invitees.length} Invite(s)`}
        </button>
      </motion.div>
    </div>
  );
};

export default InviteGuestsModal;
